import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { withTranslation } from 'react-i18next';

import { actions as authActions } from 'state/auth';
import { actions as uiActions } from 'state/ui';

import { isEmpty, isStringAndNotNull } from 'utils/types';
import { isEmailValid } from 'utils/auth';
import { areAllTrue } from 'utils/array';
import api from 'api';

import { userIsAuthenticatedRedir } from 'components/auth/hoc';
import UserModal from 'components/UserModal/styled';

const validateForm = ({ first_name, last_name, phone, email }) => ({
  first_name: isStringAndNotNull(first_name),
  last_name: isStringAndNotNull(last_name),
  email: isEmailValid(email),
  phone: isEmpty(phone) || /^\+?[0-9]\d{1,14}$/.test(phone),
});

const enhance = compose(
  withTranslation('InviteUserModal'),
  connect(() => ({}), {
    sendUserInvitation: authActions.sendUserInvitation,
    setFeedback: uiActions.setFeedback,
  }),
  withStateHandlers(
    ({ first_name, last_name, phone, email }) => ({
      first_name: first_name || '',
      last_name: last_name || '',
      phone: phone || '',
      email: email || '',
      errors: {},
      warnings: {},
    }),
    {
      handleFieldChange:
        () =>
        (_, { name, value }) => ({
          [name]: value,
        }),
      setError:
        ({ errors }) =>
        ({ field, error }) => ({
          errors: { ...errors, [field]: error },
        }),
      setWarning:
        ({ warnings }) =>
        ({ field, warning }) => ({
          warnings: { ...warnings, [field]: warning },
        }),
    }
  ),
  withHandlers({
    handleFieldChange:
      ({ handleFieldChange }) =>
      (_, { name, value }) => {
        handleFieldChange(_, { name, value });
      },
    handleEdit:
      ({ onUserEdited, user, first_name, last_name, phone, onClose }) =>
      async () => {
        if (!user) return;

        try {
          const { data } = await api.updateUser({
            userId: user.id,
            userData: {
              first_name,
              last_name,
              phone,
            },
          });

          onUserEdited(data);
          onClose();
        } catch (e) {
          console.error(e);
        }
      },
    handleSendInvitation:
      ({
        first_name,
        last_name,
        phone,
        email,
        setError,
        setWarning,
        onClose,
        shipperId,
        onCreateUser,
        setFeedback,
      }) =>
      async () => {
        try {
          const validations = validateForm({
            first_name,
            last_name,
            phone,
            email,
          });
          const isValid = areAllTrue(
            Object.entries(validations).map((v) => v[1])
          );

          if (isValid) {
            const invitationUserData = {
              first_name,
              last_name,
              phone,
              username: email,
            };

            const userCreated = await api.sendUserInvitation({
              shipperId: shipperId,
              invitationUserData,
            });

            onCreateUser(userCreated);

            setFeedback({
              feedback: 'InviteUserModal:send_action.success',
              params: {
                email: invitationUserData.username,
              },
            });

            onClose();
          } else {
            Object.entries(validations).map((v) => {
              const fieldName = v[0];
              const isFieldValid = v[1];

              if (!isFieldValid) {
                ['phone', 'email'].indexOf(fieldName) >= 0
                  ? setError({ field: fieldName, error: 'invalid' })
                  : setWarning({ field: fieldName, warning: 'required' });
              }

              return true;
            });
          }
        } catch (e) {
          console.error(e);
          setFeedback({
            feedback: 'InviteUserModal:send_action.error',
            success: false,
          });
        } finally {
          setTimeout(() => {
            setFeedback({ feedback: null });
          }, 5000);
        }
      },
  }),
  lifecycle({
    componentDidMount() {
      const { user, handleFieldChange } = this.props;

      if (!!user) {
        handleFieldChange(null, { name: 'first_name', value: user.first_name });
        handleFieldChange(null, { name: 'last_name', value: user.last_name });
        handleFieldChange(null, { name: 'phone', value: user.phone });
        handleFieldChange(null, { name: 'email', value: user.email });
      }
    },
  }),
  userIsAuthenticatedRedir
);

export default enhance(UserModal);
